import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Copy, Check, Wallet, Loader2, ShieldAlert, ShieldCheck, QrCode, ExternalLink } from 'lucide-react';

const tg = () => window.Telegram?.WebApp;

const CopyRow = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[12px] text-white font-mono break-all">{value}</code>
        <button
          data-testid={`copy-${testid}`}
          onClick={copy}
          className="text-slate-400 hover:text-emerald-400 transition p-1"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const fmtCountdown = (sec) => {
  const m = Math.max(0, Math.floor(sec / 60));
  const s = Math.max(0, sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * ClaimWalletDialog
 *
 * Triggered when /api/auth/link-wallet returns 409 with detail.can_claim === true.
 *
 * Steps:
 *  1. Confirm intent — explain why & start the claim.
 *  2. Show memo + platform address + amount (copyable); wait for user to send 1 drop with memo.
 *  3. Auto-poll /verify every 6s OR on "I sent it" tap; transition to success / awaiting_admin / expired.
 */
export default function ClaimWalletDialog({ open, onOpenChange, xrplAddress, onSuccess }) {
  const [step, setStep] = useState('intro');   // intro | proof | done | admin
  const [claim, setClaim] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [polling, setPolling] = useState(false);
  const [doneTxHash, setDoneTxHash] = useState('');
  const [adminMsg, setAdminMsg] = useState('');
  const pollTimerRef = useRef(null);
  // Refs for values read inside the polling closure. Using refs (not state)
  // means the auto-poll effect doesn't restart on every poll cycle, AND the
  // closure always reads the latest values without us listing them as deps.
  const pollingRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setStep('intro');
      setClaim(null);
      setSecondsLeft(0);
      setPolling(false);
      setDoneTxHash('');
      setAdminMsg('');
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    }
  }, [open]);

  // Countdown
  useEffect(() => {
    if (step !== 'proof' || !claim) return;
    const tick = () => {
      const left = Math.floor((new Date(claim.expires_at) - new Date()) / 1000);
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(t);
        toast.error('Claim window expired. Start a new claim.');
        setStep('intro');
        setClaim(null);
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [step, claim]);

  // Auto-poll verify every 6s while in proof step.
  // Deps intentionally exclude `polling`/`onSuccess` — both are accessed via refs
  // (pollingRef, onSuccessRef) so the interval is created exactly once per
  // (step, claim) transition instead of churning every poll cycle.
  useEffect(() => {
    if (step !== 'proof' || !claim) return;
    const poll = async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      setPolling(true);
      try {
        const r = await axios.post(`${API}/wallet/claim/verify`, { claim_id: claim.claim_id });
        const data = r.data || {};
        if (data.status === 'completed') {
          setDoneTxHash(data.tx_hash || '');
          setStep('done');
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          onSuccessRef.current?.(claim.xrpl_address);
        } else if (data.status === 'awaiting_admin') {
          setDoneTxHash(data.tx_hash || '');
          setAdminMsg(data.message || 'Awaiting admin approval.');
          setStep('admin');
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        }
        // else status='pending' → keep polling silently
      } catch (e) {
        // If claim expired or other terminal error, surface once and stop.
        // Transient network errors fall through silently and we retry on next tick.
        const detail = e.response?.data?.detail;
        if (detail && /expired|not found|not your/i.test(detail)) {
          toast.error(detail);
          setStep('intro');
          setClaim(null);
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        }
      } finally {
        pollingRef.current = false;
        setPolling(false);
      }
    };
    pollTimerRef.current = setInterval(poll, 6000);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [step, claim]);

  const startClaim = async () => {
    try {
      const r = await axios.post(`${API}/wallet/claim/initiate`, { xrpl_address: xrplAddress });
      setClaim(r.data);
      setStep('proof');
      try { tg()?.HapticFeedback?.impactOccurred('medium'); } catch { /* tg api unavailable outside Telegram */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start claim');
    }
  };

  const manualCheck = async () => {
    if (!claim) return;
    setPolling(true);
    try {
      const r = await axios.post(`${API}/wallet/claim/verify`, { claim_id: claim.claim_id });
      const data = r.data || {};
      if (data.status === 'completed') {
        setDoneTxHash(data.tx_hash || '');
        setStep('done');
        onSuccess?.(claim.xrpl_address);
      } else if (data.status === 'awaiting_admin') {
        setDoneTxHash(data.tx_hash || '');
        setAdminMsg(data.message || 'Awaiting admin approval.');
        setStep('admin');
      } else {
        toast.message('No matching transaction yet. Wait ~10 seconds after sending.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verify failed');
    } finally {
      setPolling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="claim-wallet-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
        {step === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-amber-400" /> Claim This Wallet
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                This wallet is already linked to another account. Prove you own it to transfer it to your account.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-slate-800/60 rounded-lg p-3 text-xs text-slate-300 space-y-2">
                <p><span className="text-amber-300 font-semibold">How it works:</span></p>
                <ol className="list-decimal pl-4 space-y-1 text-slate-400">
                  <li>We give you a unique memo + our platform address.</li>
                  <li>You send <strong className="text-white">0.000001 XRP</strong> (1 drop) from this wallet with that memo.</li>
                  <li>We detect it on-chain → wallet is moved to your account.</li>
                </ol>
                <p className="text-[10px] text-slate-500 pt-1">
                  Only the holder of this wallet can sign the proof tx. The 1 drop is the network's minimum — effectively free.
                </p>
              </div>
              <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-2.5">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Claiming</p>
                <code className="text-[11px] text-white font-mono break-all">{xrplAddress}</code>
              </div>
              <Button
                data-testid="claim-start"
                onClick={startClaim}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-10"
              >
                Start Claim
              </Button>
            </div>
          </>
        )}

        {step === 'proof' && claim && (() => {
          // Build Xaman deeplink: works in both Xaman mobile app and web.
          // Format: https://xumm.app/detect/request:{addr}?amount={XRP}&memo={text}
          // Xaman parses memo as text and embeds it as MemoData on signing.
          const xamanUrl = `https://xumm.app/detect/request:${claim.platform_address}?amount=${claim.amount_xrp}&memo=${encodeURIComponent('VCG-' + claim.memo)}`;
          // QR encodes the same URL so any Xaman/XRPL wallet that supports QR can scan it.
          return (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-amber-400" /> Send Proof Transaction
                </DialogTitle>
                <DialogDescription className="text-slate-400 text-xs">
                  From <code className="font-mono">{xrplAddress.slice(0, 8)}…{xrplAddress.slice(-4)}</code> to our platform.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                  <span className="text-[11px] text-amber-200">Window closes in</span>
                  <span data-testid="claim-countdown" className="text-base font-mono font-bold text-amber-300 tabular-nums">
                    {fmtCountdown(secondsLeft)}
                  </span>
                </div>

                {/* QR + One-tap Xaman */}
                <div className="bg-white rounded-xl p-3 flex flex-col items-center">
                  <QRCodeSVG
                    data-testid="claim-qr"
                    value={xamanUrl}
                    size={170}
                    level="M"
                    includeMargin={false}
                  />
                  <p className="text-[10px] text-slate-700 mt-2 flex items-center gap-1">
                    <QrCode className="w-3 h-3" /> Scan with Xaman / XRPL wallet
                  </p>
                </div>

                <a
                  data-testid="claim-open-xaman"
                  href={xamanUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-500 hover:bg-blue-400 text-white font-bold text-center py-2.5 rounded-md text-sm transition"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <ExternalLink className="w-4 h-4" /> Open in Xaman
                  </span>
                </a>

                <details className="bg-slate-800/40 border border-slate-700/50 rounded-lg" data-testid="claim-manual-details">
                  <summary className="text-[11px] text-slate-300 px-3 py-2 cursor-pointer select-none">
                    Or send manually
                  </summary>
                  <div className="space-y-2 p-2">
                    <CopyRow label="Send To" value={claim.platform_address} testid="platform-addr" />
                    <CopyRow label="Amount" value={`${claim.amount_xrp} XRP`} testid="amount" />
                    <CopyRow label="Memo (MemoData)" value={`VCG-${claim.memo}`} testid="memo" />
                  </div>
                </details>

                {claim.old_account_warning && (
                  <div data-testid="claim-admin-warn" className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg p-2.5">
                    <ShieldAlert className="w-3.5 h-3.5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[10.5px] text-orange-200">{claim.old_account_warning}</p>
                  </div>
                )}

                <Button
                  data-testid="claim-check"
                  onClick={manualCheck}
                  disabled={polling}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold h-10"
                >
                  {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : "I sent it — Check now"}
                </Button>
                <p className="text-[10px] text-slate-500 text-center">
                  We're also auto-checking every 6s. You don't have to keep this open.
                </p>
              </div>
            </>
          );
        })()}

        {step === 'done' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" /> Wallet Claimed
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                Ownership verified and transferred to your account.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                <ShieldCheck className="w-8 h-8 text-emerald-400 mx-auto mb-1" />
                <p className="text-sm font-semibold text-emerald-300">Linked!</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{xrplAddress}</p>
              </div>
              {doneTxHash && (
                <div className="bg-slate-800/60 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Proof Tx</p>
                  <code className="text-[10px] text-white font-mono break-all">{doneTxHash}</code>
                </div>
              )}
              <Button
                data-testid="claim-done-close"
                onClick={() => onOpenChange(false)}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold h-10"
              >
                Done
              </Button>
            </div>
          </>
        )}

        {step === 'admin' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-orange-400" /> Awaiting Admin Review
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                Proof verified — the previous account had activity, so an admin will finalize.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p data-testid="claim-admin-msg" className="text-xs text-slate-300 bg-slate-800/60 rounded-lg p-2.5">{adminMsg}</p>
              {doneTxHash && (
                <div className="bg-slate-800/60 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Proof Tx</p>
                  <code className="text-[10px] text-white font-mono break-all">{doneTxHash}</code>
                </div>
              )}
              <Button
                data-testid="claim-admin-close"
                onClick={() => onOpenChange(false)}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 h-10"
              >
                OK, I'll check back later
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
